import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { fetchVapidPublicKey, subscribePush, unsubscribePush } from '../api/push'

/**
 * Convert a Base64Url-encoded VAPID public key into the Uint8Array that
 * `PushManager.subscribe` expects for `applicationServerKey`.
 */
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const output = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i)
  return output
}

const arrayBufferToBase64 = (buffer: ArrayBuffer | null): string => {
  if (!buffer) return ''
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary)
}

export type PushPermissionStatus = 'default' | 'granted' | 'denied' | 'unsupported'

export interface UsePushNotificationsResult {
  isSupported: boolean
  permission: PushPermissionStatus
  isSubscribed: boolean
  isWorking: boolean
  error: string | null
  subscribe: () => Promise<boolean>
  unsubscribe: () => Promise<void>
}

const detectSupport = (): boolean => {
  if (typeof window === 'undefined') return false
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

// Shared state across every consumer of the hook so a subscribe/unsubscribe in
// one component is immediately reflected in every other toggle on the page.
interface PushState {
  isSupported: boolean
  permission: PushPermissionStatus
  isSubscribed: boolean
  isWorking: boolean
  error: string | null
}

const supported = detectSupport()
let state: PushState = {
  isSupported: supported,
  permission: supported ? (Notification.permission as PushPermissionStatus) : 'unsupported',
  isSubscribed: false,
  isWorking: false,
  error: null,
}

const listeners = new Set<() => void>()
const setState = (patch: Partial<PushState>) => {
  state = { ...state, ...patch }
  listeners.forEach((l) => l())
}
const getSnapshot = () => state
const subscribeToStore = (cb: () => void) => {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

let hydrated = false
const hydrateOnce = async () => {
  if (hydrated || !state.isSupported) return
  hydrated = true
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    setState({ isSubscribed: !!sub })
  } catch {
    setState({ isSubscribed: false })
  }
}

const subscribeAction = async (): Promise<boolean> => {
  if (!state.isSupported) {
    setState({ error: 'Push notifications are not supported on this browser.' })
    return false
  }
  setState({ isWorking: true, error: null })
  try {
    let perm: PushPermissionStatus = Notification.permission as PushPermissionStatus
    if (perm === 'default') {
      perm = (await Notification.requestPermission()) as PushPermissionStatus
    }
    setState({ permission: perm })
    if (perm !== 'granted') {
      setState({ error: 'Notification permission was not granted.' })
      return false
    }

    const registration = await navigator.serviceWorker.ready
    let subscription = await registration.pushManager.getSubscription()
    if (!subscription) {
      const publicKey = await fetchVapidPublicKey()
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })
    }

    const p256dh = arrayBufferToBase64(subscription.getKey('p256dh'))
    const auth = arrayBufferToBase64(subscription.getKey('auth'))
    await subscribePush({
      endpoint: subscription.endpoint,
      keys: { p256dh, auth },
    })

    setState({ isSubscribed: true })
    return true
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to enable notifications.'
    setState({ error: message })
    return false
  } finally {
    setState({ isWorking: false })
  }
}

const unsubscribeAction = async (): Promise<void> => {
  if (!state.isSupported) return
  setState({ isWorking: true, error: null })
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) {
      await unsubscribePush(subscription.endpoint).catch(() => {
        // ignore backend failure — still try to drop browser subscription
      })
      await subscription.unsubscribe()
    }
    setState({ isSubscribed: false })
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to disable notifications.'
    setState({ error: message })
  } finally {
    setState({ isWorking: false })
  }
}

/**
 * Web Push registration flow with shared state across all hook consumers, so multiple
 * toggles on the same page stay in sync.
 */
export const usePushNotifications = (): UsePushNotificationsResult => {
  const snapshot = useSyncExternalStore(subscribeToStore, getSnapshot, getSnapshot)

  useEffect(() => {
    void hydrateOnce()
  }, [])

  const subscribe = useCallback(() => subscribeAction(), [])
  const unsubscribe = useCallback(() => unsubscribeAction(), [])

  return {
    isSupported: snapshot.isSupported,
    permission: snapshot.permission,
    isSubscribed: snapshot.isSubscribed,
    isWorking: snapshot.isWorking,
    error: snapshot.error,
    subscribe,
    unsubscribe,
  }
}
